import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  MenuToggle,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  OutlinedCircleIcon,
  SearchIcon,
  SyncAltIcon,
  UsersIcon,
} from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useNewsletterAudience } from '../../../hooks/useNewsletterAudience';
import { NewsletterAudienceUser, UserAccountStatus } from '../../../types/models';

type LabelColor = React.ComponentProps<typeof Label>['color'];

/** Newsletter opt-in filter: all recipients, only opted-in, or only opted-out. */
type NewsletterFilter = 'all' | 'in' | 'out';

const NEWSLETTER_FILTER_LABEL: Record<NewsletterFilter, string> = {
  all: 'All recipients',
  in: 'Opted in',
  out: 'Opted out',
};

const isNewsletterFilter = (v: unknown): v is NewsletterFilter => v === 'all' || v === 'in' || v === 'out';

// Account-status → label styling. Colours reinforce the (always-present) text,
// which by itself conveys the status, so no icon is required for accessibility.
const ACCOUNT_STATUS_META: Record<UserAccountStatus, { color: LabelColor; text: string }> = {
  valid: { color: 'green', text: 'Valid' },
  invited: { color: 'blue', text: 'Invited' },
  registered: { color: 'grey', text: 'Registered' },
  blocked: { color: 'red', text: 'Blocked' },
  unknown: { color: 'grey', text: 'Unknown' },
};

const renderAccountStatus = (status: UserAccountStatus) => {
  // Defensive fallback in case the backend ever returns a status outside the
  // documented set — render it grey with the raw value capitalised.
  const meta = ACCOUNT_STATUS_META[status] ?? {
    color: 'grey' as LabelColor,
    text: status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : 'Unknown',
  };
  return (
    <Label isCompact color={meta.color}>
      {meta.text}
    </Label>
  );
};

const renderNewsletter = (optedIn: boolean) =>
  optedIn ? (
    <Label isCompact color="green" icon={<CheckCircleIcon />}>
      Opted in
    </Label>
  ) : (
    <Label isCompact color="grey" icon={<OutlinedCircleIcon />}>
      Opted out
    </Label>
  );

const displayName = (user: NewsletterAudienceUser): string =>
  [user.firstname, user.lastname].filter((part) => part && part.trim() !== '').join(' ').trim();

// Subtle em-dash for empty cells. Uses the PF6 subtle-text design token — NOT
// the legacy `pf-v6-u-color-200` utility, which is a no-op in PF6.
const MutedDash = () => <span style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>—</span>;

/**
 * NewsletterRecipientsTab — the read-only audience for the Ancalagon-staff-only
 * Newsletter admin page.
 *
 * Lists every user across all tenants with their resolved newsletter opt-in
 * preference. Users set their own opt-in via `PUT /users/me/preferences`; there
 * is no admin write path, so this table is display-only.
 *
 * The endpoint returns the full list in one call, so filtering (search across
 * name/email/status + an opt-in dropdown) and pagination are all client-side
 * (Mode A). It fetches its own data, so the parent `<Tabs>` sets `mountOnEnter`
 * and this tab exposes a Refresh action for manual re-runs.
 */
function NewsletterRecipientsTab() {
  const { users, loading, error, refetch } = useNewsletterAudience();

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [newsletterFilter, setNewsletterFilter] = useState<NewsletterFilter>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // Debounce the search so filtering doesn't run on every keystroke; reset to
  // the first page whenever the effective query changes.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  // Reset to page 1 whenever the newsletter filter changes, otherwise the user
  // can be left stranded on a now out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [newsletterFilter]);

  const filteredUsers = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return users.filter((user) => {
      if (newsletterFilter === 'in' && !user.newsletter_opt_in) return false;
      if (newsletterFilter === 'out' && user.newsletter_opt_in) return false;
      if (q) {
        const haystack = [user.firstname, user.lastname, user.email, user.status]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [users, debouncedSearch, newsletterFilter]);

  // Clamp the page when the filtered set shrinks below it — e.g. a manual
  // Refresh returns fewer users, or perPage grows. Without this the admin can be
  // stranded on an out-of-range page rendering an empty table body (the
  // "no matches" empty state doesn't fire while filteredUsers.length > 0).
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredUsers.length / perPage));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredUsers.length, perPage, page]);

  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredUsers.slice(start, start + perPage);
  }, [filteredUsers, page, perPage]);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setNewsletterFilter('all');
    setPage(1);
  };

  // Initial load only. On a manual refresh, `users` is already populated, so the
  // table stays on screen (and the Refresh button shows its own spinner) instead
  // of collapsing into this full-panel spinner.
  if (loading && users.length === 0) {
    return <CenteredSpinner />;
  }

  if (error) {
    return (
      <EmptyState
        titleText="Unable to load recipients"
        icon={ExclamationCircleIcon}
        variant="lg"
        headingLevel="h2"
        status="danger"
      >
        <EmptyStateBody>{error.message}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" icon={<SyncAltIcon />} onClick={() => void refetch()}>
              Retry
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  if (users.length === 0) {
    return (
      <EmptyState titleText="No users yet" icon={UsersIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>
          There are no users to show. Once people register, they will appear here with their newsletter preference.
        </EmptyStateBody>
      </EmptyState>
    );
  }

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by name, email, or status..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          <ToolbarItem>
            <Select
              isOpen={isFilterOpen}
              selected={newsletterFilter}
              onSelect={(_event, value) => {
                if (isNewsletterFilter(value)) {
                  setNewsletterFilter(value);
                }
                setIsFilterOpen(false);
              }}
              onOpenChange={(isOpen) => setIsFilterOpen(isOpen)}
              toggle={(toggleRef) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  isExpanded={isFilterOpen}
                  aria-label="Filter by newsletter status"
                >
                  {NEWSLETTER_FILTER_LABEL[newsletterFilter]}
                </MenuToggle>
              )}
            >
              <SelectList>
                <SelectOption value="all">All recipients</SelectOption>
                <SelectOption value="in">Opted in</SelectOption>
                <SelectOption value="out">Opted out</SelectOption>
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <Button
              variant="secondary"
              icon={<SyncAltIcon />}
              onClick={() => void refetch()}
              isLoading={loading}
              isDisabled={loading}
            >
              Refresh
            </Button>
          </ToolbarItem>
          <ToolbarItem variant="pagination">
            <Pagination
              itemCount={filteredUsers.length}
              perPage={perPage}
              page={page}
              onSetPage={onSetPage}
              onPerPageSelect={onPerPageSelect}
            />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {filteredUsers.length === 0 ? (
        <EmptyState titleText="No recipients match your filters" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Try a different search term, or clear the filters to see everyone.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={clearFilters}>
                Clear filters
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      ) : (
        <>
          <Table aria-label="Newsletter recipients" variant="compact">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Account status</Th>
                <Th>Newsletter</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedUsers.map((user) => {
                const name = displayName(user);
                return (
                  <Tr key={user.id ?? user.email ?? name}>
                    <Td dataLabel="Name">{name !== '' ? name : <MutedDash />}</Td>
                    <Td dataLabel="Email">{user.email ?? <MutedDash />}</Td>
                    <Td dataLabel="Account status">{renderAccountStatus(user.status)}</Td>
                    <Td dataLabel="Newsletter">{renderNewsletter(user.newsletter_opt_in)}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>

          <Pagination
            itemCount={filteredUsers.length}
            perPage={perPage}
            page={page}
            onSetPage={onSetPage}
            onPerPageSelect={onPerPageSelect}
            variant="bottom"
          />
        </>
      )}
    </>
  );
}

export default NewsletterRecipientsTab;
